import { useState, useRef, useCallback, useEffect } from "react";

// ── Design tokens ────────────────────────────────────────────────────────────
const NAVY = '#0C2D48';
const YELLOW = '#e4e69b';
const GREEN = '#27ae60';
const BORDER = '#e0e0e0';
const BG = '#f8f9fb';

// ── Helpers ───────────────────────────────────────────────────────────────────
function uid() { return Math.random().toString(36).slice(2, 10); }

function makeBlock(type = 'paragraph', html = '') {
  return { id: uid(), type, html, checked: false, src: '', alt: '' };
}

function escapeHtml(str) {
  return String(str || '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function stripHtml(html) {
  const d = document.createElement('div');
  d.innerHTML = html || '';
  return d.textContent || '';
}

export function textToBlocks(text) {
  if (!text || !text.trim()) return [makeBlock()];
  return text.split('\n').map(l => makeBlock('paragraph', escapeHtml(l)));
}

export function blocksToText(blocks) {
  return (blocks || []).map(b => {
    if (b.type === 'divider') return '---';
    if (b.type === 'image') return `[Image: ${b.alt || b.src || ''}]`;
    const prefix =
      b.type === 'checkbox' ? (b.checked ? '[x] ' : '[ ] ') :
        b.type === 'bullet' ? '• ' :
          b.type === 'numbered' ? '1. ' : '';
    return prefix + stripHtml(b.html);
  }).filter(Boolean).join('\n');
}

// ── Slash commands ─────────────────────────────────────────────────────────────
const SLASH_CMDS = [
  { type: 'h1', icon: 'H1', label: 'Heading 1', desc: 'Big section heading' },
  { type: 'h2', icon: 'H2', label: 'Heading 2', desc: 'Medium section heading' },
  { type: 'h3', icon: 'H3', label: 'Heading 3', desc: 'Small section heading' },
  { type: 'paragraph', icon: 'T', label: 'Text', desc: 'Plain paragraph' },
  { type: 'bullet', icon: '•', label: 'Bullet List', desc: 'Unordered list item' },
  { type: 'numbered', icon: '1.', label: 'Numbered List', desc: 'Ordered list item' },
  { type: 'checkbox', icon: '☐', label: 'To-Do', desc: 'Checkbox task' },
  { type: 'quote', icon: '"', label: 'Quote', desc: 'Highlighted callout' },
  { type: 'code', icon: '<>', label: 'Code', desc: 'Monospace code block' },
  { type: 'divider', icon: '—', label: 'Divider', desc: 'Horizontal rule' },
  { type: 'image', icon: '🖼', label: 'Image', desc: 'Upload an image' },
];

// ── Emoji list ─────────────────────────────────────────────────────────────────
const EMOJIS = [
  '😀', '😃', '😄', '😁', '😅', '🤣', '😊', '🥰', '😍', '🤩', '😎', '🥳', '😇', '🤔', '🙄', '😏',
  '😢', '😭', '😡', '🤬', '😤', '😱', '🥵', '🥶', '🤯', '💀', '👻', '🤖', '💩', '👋', '✌️', '👍',
  '👎', '👏', '🙌', '🙏', '💪', '❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '💔', '✨', '🔥', '⭐',
  '🌟', '💫', '🎉', '🎊', '🎁', '🏆', '🥇', '💡', '📝', '📌', '📎', '💰', '💎', '⚡', '🌈', '☀️',
  '🌊', '❄️', '🍎', '🍕', '🍔', '☕', '🍺', '🚀', '🎮', '🎵', '🎬', '📱', '💻', '🖥', '🔑', '🔒',
  '📅', '📊', '📈', '✅', '❌', '⚠️', 'ℹ️', '🔴', '🟡', '🟢', '🔵', '⚫', '⚪', '🟤', '🟣', '🟠',
];

// ── Format toolbar ──────────────────────────────────────────────────────────
function FormatToolbar({ rect, onFormat, onLink, onClose }) {
  const [linkMode, setLinkMode] = useState(false);
  const [url, setUrl] = useState('https://');
  const ref = useRef();

  useEffect(() => {
    function onMouseDown(e) {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    }
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [onClose]);

  const top = rect.top - 48 + window.scrollY;
  const left = Math.max(8, rect.left + window.scrollX);

  const btn = (cmd, label, title) => (
    <button
      key={cmd}
      title={title}
      onMouseDown={e => { e.preventDefault(); onFormat(cmd); }}
      style={tbBtn}
    >{label}</button>
  );

  return (
    <div ref={ref} style={{ ...tbWrap, top, left }}>
      {!linkMode ? (
        <>
          {btn('bold', <b>B</b>, 'Bold')}
          {btn('italic', <i>I</i>, 'Italic')}
          {btn('underline', <u>U</u>, 'Underline')}
          {btn('strikeThrough', <s>S</s>, 'Strikethrough')}
          <div style={{ width: 1, background: 'rgba(255,255,255,0.2)', margin: '0 4px' }} />
          <button title="Link" onMouseDown={e => { e.preventDefault(); setLinkMode(true); }} style={tbBtn}>🔗</button>
          <button title="Remove link" onMouseDown={e => { e.preventDefault(); onFormat('unlink'); }} style={tbBtn}>✂️</button>
          <div style={{ width: 1, background: 'rgba(255,255,255,0.2)', margin: '0 4px' }} />
          <button title="Highlight" onMouseDown={e => { e.preventDefault(); onFormat('hiliteColor', YELLOW); }} style={tbBtn}>🖊</button>
          <button title="Clear format" onMouseDown={e => { e.preventDefault(); onFormat('removeFormat'); }} style={{ ...tbBtn, fontSize: '0.75rem' }}>✕fmt</button>
        </>
      ) : (
        <>
          <input
            autoFocus
            value={url}
            onChange={e => setUrl(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') { onLink(url); setLinkMode(false); }
              if (e.key === 'Escape') setLinkMode(false);
            }}
            placeholder="https://..."
            style={{ background: '#fff', border: 'none', borderRadius: 4, padding: '2px 6px', fontSize: '0.85rem', width: 200 }}
          />
          <button onMouseDown={e => { e.preventDefault(); onLink(url); setLinkMode(false); }} style={{ ...tbBtn, background: GREEN }}>Apply</button>
          <button onMouseDown={e => { e.preventDefault(); setLinkMode(false); }} style={tbBtn}>✕</button>
        </>
      )}
    </div>
  );
}

const tbWrap = {
  position: 'fixed', zIndex: 9999,
  display: 'flex', alignItems: 'center', gap: 2,
  background: '#1e1e2e', color: '#fff',
  borderRadius: 8, padding: '4px 8px',
  boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
  fontFamily: 'Raleway, sans-serif',
};
const tbBtn = {
  background: 'transparent', border: 'none', color: '#fff',
  cursor: 'pointer', padding: '3px 7px', borderRadius: 4,
  fontSize: '0.9rem', lineHeight: 1.4,
};

// ── Slash command menu ────────────────────────────────────────────────────────
function SlashMenu({ x, y, query, onSelect, onClose }) {
  const ref = useRef();
  const [sel, setSel] = useState(0);

  const matches = SLASH_CMDS.filter(c =>
    !query || c.label.toLowerCase().includes(query.toLowerCase()) || c.type.includes(query.toLowerCase())
  );

  useEffect(() => { setSel(0); }, [query]);

  useEffect(() => {
    function onKey(e) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setSel(s => Math.min(s + 1, matches.length - 1)); }
      if (e.key === 'ArrowUp') { e.preventDefault(); setSel(s => Math.max(s - 1, 0)); }
      if (e.key === 'Enter') { e.preventDefault(); if (matches[sel]) onSelect(matches[sel].type); }
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [matches, sel, onSelect, onClose]);

  if (!matches.length) return null;

  return (
    <div ref={ref} style={{
      position: 'fixed', zIndex: 9999, left: x, top: y,
      background: '#fff', border: `1px solid ${BORDER}`,
      borderRadius: 10, boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
      minWidth: 240, maxHeight: 320, overflowY: 'auto',
      fontFamily: 'Raleway, sans-serif',
    }}>
      <div style={{ padding: '6px 10px 4px', fontSize: '0.72rem', color: '#999', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        Blocks
      </div>
      {matches.map((c, i) => (
        <div
          key={c.type}
          onMouseDown={e => { e.preventDefault(); onSelect(c.type); }}
          style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '7px 12px', cursor: 'pointer',
            background: i === sel ? BG : 'transparent',
            borderRadius: 6, margin: '1px 4px',
          }}
        >
          <span style={{
            width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: '#f0f0f0', borderRadius: 6, fontSize: '0.9rem', fontWeight: 700, flexShrink: 0,
          }}>{c.icon}</span>
          <span>
            <div style={{ fontWeight: 600, fontSize: '0.88rem', color: '#222' }}>{c.label}</div>
            <div style={{ fontSize: '0.78rem', color: '#999' }}>{c.desc}</div>
          </span>
        </div>
      ))}
    </div>
  );
}

// ── Emoji picker ──────────────────────────────────────────────────────────────
function EmojiPicker({ onPick, onClose }) {
  const ref = useRef();
  useEffect(() => {
    function onDown(e) { if (ref.current && !ref.current.contains(e.target)) onClose(); }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [onClose]);

  return (
    <div ref={ref} style={{
      position: 'absolute', top: '110%', left: 0, zIndex: 8000,
      background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 10,
      boxShadow: '0 8px 30px rgba(0,0,0,0.12)', padding: 10, width: 280,
    }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
        {EMOJIS.map(e => (
          <button key={e} onClick={() => onPick(e)} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            fontSize: '1.3rem', padding: '3px', borderRadius: 4,
            lineHeight: 1,
          }}>{e}</button>
        ))}
      </div>
    </div>
  );
}

// ── Single block renderer ─────────────────────────────────────────────────────
function Block({ block, blockRef, onKeyDown, onInput, onChange, onImageUpload, isOnly, placeholder }) {
  const imgRef = useRef();

  if (block.type === 'divider') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '0.5rem 0', cursor: 'default' }}>
        <hr style={{ flex: 1, border: 'none', borderTop: `2px solid ${BORDER}` }} />
        <span style={{ fontSize: '0.75rem', color: '#ccc' }}>§</span>
        <hr style={{ flex: 1, border: 'none', borderTop: `2px solid ${BORDER}` }} />
      </div>
    );
  }

  if (block.type === 'image') {
    return (
      <div style={{ margin: '0.5rem 0' }}>
        {block.src ? (
          <div style={{ position: 'relative' }}>
            <img
              src={block.src}
              alt={block.alt || 'image'}
              style={{ maxWidth: '100%', borderRadius: 8, display: 'block', boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}
            />
            <input
              placeholder="Add caption..."
              value={block.alt || ''}
              onChange={e => onChange({ ...block, alt: e.target.value })}
              style={{
                display: 'block', width: '100%', border: 'none', borderTop: `1px solid ${BORDER}`,
                padding: '4px 8px', fontSize: '0.82rem', color: '#888', textAlign: 'center',
                background: 'transparent', outline: 'none', marginTop: 4, boxSizing: 'border-box',
              }}
            />
            <button
              onClick={() => onImageUpload(block.id)}
              title="Change image"
              style={{ position: 'absolute', top: 8, right: 8, background: 'rgba(0,0,0,0.5)', color: '#fff', border: 'none', borderRadius: 4, padding: '3px 8px', cursor: 'pointer', fontSize: '0.75rem' }}
            >Change</button>
          </div>
        ) : (
          <div
            onClick={() => onImageUpload(block.id)}
            style={{
              border: `2px dashed ${BORDER}`, borderRadius: 8, padding: '2rem',
              textAlign: 'center', cursor: 'pointer', color: '#bbb',
              background: BG, fontSize: '0.9rem',
            }}
          >
            🖼 Click to upload image
          </div>
        )}
        <input ref={imgRef} type="file" accept="image/*" style={{ display: 'none' }}
          onChange={e => {
            const file = e.target.files?.[0];
            if (!file) return;
            const reader = new FileReader();
            reader.onload = ev => onChange({ ...block, src: ev.target.result });
            reader.readAsDataURL(file);
          }}
        />
      </div>
    );
  }

  if (block.type === 'checkbox') {
    return (
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, margin: '2px 0' }}>
        <input
          type="checkbox"
          checked={!!block.checked}
          onChange={e => onChange({ ...block, checked: e.target.checked })}
          style={{ marginTop: 4, width: 16, height: 16, cursor: 'pointer', accentColor: GREEN }}
        />
        <div
          ref={blockRef}
          contentEditable
          suppressContentEditableWarning
          data-block-id={block.id}
          onKeyDown={e => onKeyDown(e, block.id)}
          onInput={e => onInput(e, block.id)}
          data-placeholder={placeholder || 'To-do...'}
          style={{
            ...ceBase,
            flex: 1,
            textDecoration: block.checked ? 'line-through' : 'none',
            color: block.checked ? '#aaa' : '#222',
          }}
          dangerouslySetInnerHTML={{ __html: block.html }}
        />
      </div>
    );
  }

  const styles = {
    paragraph: { ...ceBase, fontSize: '1rem', lineHeight: 1.7 },
    h1: { ...ceBase, fontSize: '2rem', fontWeight: 800, lineHeight: 1.25, color: NAVY, marginBottom: 2 },
    h2: { ...ceBase, fontSize: '1.55rem', fontWeight: 700, lineHeight: 1.3, color: NAVY },
    h3: { ...ceBase, fontSize: '1.2rem', fontWeight: 700, lineHeight: 1.4, color: NAVY },
    bullet: { ...ceBase, paddingLeft: 0, listStyleType: 'none' },
    numbered: { ...ceBase, paddingLeft: 0, listStyleType: 'none' },
    quote: { ...ceBase, borderLeft: `3px solid ${YELLOW}`, paddingLeft: 16, color: '#555', fontStyle: 'italic', background: '#fafafa', borderRadius: '0 6px 6px 0', padding: '8px 16px' },
    code: { ...ceBase, fontFamily: 'monospace', background: '#1e1e2e', color: '#e0e0e0', borderRadius: 8, padding: '12px 16px', fontSize: '0.9rem', whiteSpace: 'pre' },
  };

  const bullets = { bullet: '•', numbered: '1.', };
  const style = styles[block.type] || styles.paragraph;

  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
      {(block.type === 'bullet' || block.type === 'numbered') && (
        <span style={{ marginTop: 2, color: '#888', fontWeight: 600, fontSize: '1rem', minWidth: 18, flexShrink: 0 }}>
          {bullets[block.type]}
        </span>
      )}
      <div
        ref={blockRef}
        contentEditable
        suppressContentEditableWarning
        data-block-id={block.id}
        onKeyDown={e => onKeyDown(e, block.id)}
        onInput={e => onInput(e, block.id)}
        data-placeholder={placeholder || 'Type something, or type \'/\' for commands...'}
        style={{ ...style, flex: 1, minWidth: 0 }}
        dangerouslySetInnerHTML={{ __html: block.html }}
      />
    </div>
  );
}

const ceBase = {
  outline: 'none', minHeight: '1.6em', wordBreak: 'break-word',
  fontFamily: 'Raleway, sans-serif', color: '#222',
  caretColor: NAVY,
};

// ── Main BlockEditor ──────────────────────────────────────────────────────────
export default function BlockEditor({ note, onChange }) {
  const isNew = !note.blocks || note.blocks.length === 0;
  const initialBlocks = note.blocks?.length
    ? note.blocks
    : textToBlocks(note.text);

  const [blocks, setBlocks] = useState(initialBlocks);
  const [title, setTitle] = useState(note.title || '');
  const [emoji, setEmoji] = useState(note.emoji || '');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [slash, setSlash] = useState(null); // { blockId, query, x, y }
  const [fmtBar, setFmtBar] = useState(null); // { rect }
  const [activeId, setActiveId] = useState(null);

  const blockRefs = useRef({});    // blockId → DOM element
  const pendingHtml = useRef({});   // blockId → current innerHTML (not yet in state)
  const imgInputs = useRef({});   // blockId → hidden file input
  const imageUploadTarget = useRef(null);

  // Sync to parent whenever blocks / title / emoji change
  useEffect(() => {
    const text = blocksToText(blocks);
    onChange({ ...note, title, emoji, blocks, text });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blocks, title, emoji]);

  // Keep blockRefs in sync
  const setBlockRef = useCallback((id, el) => {
    if (el) blockRefs.current[id] = el;
  }, []);

  // ── helpers ──────────────────────────────────────────────────────────────────
  function syncBlock(id) {
    const el = blockRefs.current[id];
    if (!el) return;
    const html = el.innerHTML;
    pendingHtml.current[id] = html;
    setBlocks(bs => bs.map(b => b.id === id ? { ...b, html } : b));
  }

  function flushAll() {
    setBlocks(bs => bs.map(b => {
      const el = blockRefs.current[b.id];
      if (!el) return b;
      return { ...b, html: el.innerHTML };
    }));
  }

  function insertAfter(afterId, type = 'paragraph') {
    flushAll();
    const fresh = makeBlock(type);
    setBlocks(bs => {
      const idx = bs.findIndex(b => b.id === afterId);
      const next = [...bs];
      next.splice(idx + 1, 0, fresh);
      return next;
    });
    setTimeout(() => {
      const el = blockRefs.current[fresh.id];
      if (el) { el.focus(); placeCursorAtStart(el); }
    }, 0);
  }

  function deleteBlock(id) {
    setBlocks(bs => {
      if (bs.length <= 1) return bs;
      const idx = bs.findIndex(b => b.id === id);
      const prevId = idx > 0 ? bs[idx - 1].id : null;
      const next = bs.filter(b => b.id !== id);
      setTimeout(() => {
        if (prevId) {
          const el = blockRefs.current[prevId];
          if (el) { el.focus(); placeCursorAtEnd(el); }
        }
      }, 0);
      return next;
    });
  }

  function changeType(id, newType) {
    // special: divider & image have no html content
    if (newType === 'divider' || newType === 'image') {
      setBlocks(bs => bs.map(b => b.id === id ? { ...b, type: newType, html: '' } : b));
      return;
    }
    const el = blockRefs.current[id];
    const html = el ? el.innerHTML : (pendingHtml.current[id] || '');
    setBlocks(bs => bs.map(b => b.id === id ? { ...b, type: newType, html } : b));
    setTimeout(() => {
      const nel = blockRefs.current[id];
      if (nel) { nel.focus(); placeCursorAtEnd(nel); }
    }, 0);
  }

  // ── keyboard events ──────────────────────────────────────────────────────────
  function handleKeyDown(e, id) {
    const el = blockRefs.current[id];

    if (e.key === 'Enter' && !e.shiftKey) {
      // If slash menu open, let it handle via its own keydown listener
      if (slash) return;
      e.preventDefault();

      const block = blocks.find(b => b.id === id);
      const listTypes = ['bullet', 'numbered', 'checkbox'];
      const isList = block && listTypes.includes(block.type);

      // Empty list item → exit the list (convert to paragraph)
      if (isList && el && !el.textContent.trim()) {
        changeType(id, 'paragraph');
        return;
      }

      // Split: content before cursor stays, content after goes into new block
      const sel = window.getSelection();
      let afterHtml = '';
      if (sel && el) {
        const range = sel.getRangeAt(0);
        const afterRange = range.cloneRange();
        afterRange.setEnd(el, el.childNodes.length);
        const frag = afterRange.extractContents();
        const div = document.createElement('div');
        div.appendChild(frag);
        afterHtml = div.innerHTML;
        afterRange.deleteContents();
      }
      syncBlock(id);
      // Continue same type for list blocks, otherwise paragraph
      const fresh = makeBlock(isList ? block.type : 'paragraph', afterHtml);
      setBlocks(bs => {
        const idx = bs.findIndex(b => b.id === id);
        const arr = [...bs];
        arr.splice(idx + 1, 0, fresh);
        return arr;
      });
      setTimeout(() => {
        const nel = blockRefs.current[fresh.id];
        if (nel) { nel.focus(); placeCursorAtStart(nel); }
      }, 0);
    }

    if (e.key === 'Backspace') {
      if (slash) return;
      const isEmpty = !el || el.textContent.trim() === '';
      const block = blocks.find(b => b.id === id);
      if (block?.type !== 'paragraph' && isEmpty) {
        e.preventDefault();
        changeType(id, 'paragraph');
        return;
      }
      if (isEmpty && blocks.length > 1) {
        e.preventDefault();
        deleteBlock(id);
      }
    }

    if (e.key === 'Tab') {
      e.preventDefault();
      document.execCommand('insertHTML', false, '&nbsp;&nbsp;&nbsp;&nbsp;');
    }

    if (e.key === 'Escape' && slash) {
      setSlash(null);
    }
  }

  function handleInput(e, id) {
    const el = blockRefs.current[id];
    if (!el) return;
    pendingHtml.current[id] = el.innerHTML;

    const text = el.textContent || '';

    // Show/update slash menu
    if (text.startsWith('/')) {
      const query = text.slice(1);
      const rect = el.getBoundingClientRect();
      setSlash({ blockId: id, query, x: rect.left, y: rect.bottom + 4 });
    } else if (slash?.blockId === id) {
      setSlash(null);
    }
  }

  function handleSlashSelect(type) {
    const id = slash.blockId;
    setSlash(null);
    const el = blockRefs.current[id];
    if (el) el.innerHTML = '';
    pendingHtml.current[id] = '';
    setBlocks(bs => bs.map(b => b.id === id ? { ...b, type, html: '' } : b));
    if (type !== 'divider' && type !== 'image') {
      setTimeout(() => {
        const nel = blockRefs.current[id];
        if (nel) { nel.focus(); placeCursorAtEnd(nel); }
      }, 0);
    }
    if (type === 'image') handleImageUpload(id);
  }

  // ── selection → format toolbar ───────────────────────────────────────────────
  function handleSelectionChange() {
    const sel = window.getSelection();
    if (!sel || sel.isCollapsed || !sel.toString().trim()) {
      setFmtBar(null);
      return;
    }
    const range = sel.getRangeAt(0);
    const rect = range.getBoundingClientRect();
    if (rect.width === 0) return;
    setFmtBar({ rect });
  }

  useEffect(() => {
    document.addEventListener('selectionchange', handleSelectionChange);
    return () => document.removeEventListener('selectionchange', handleSelectionChange);
  }, []);

  function execFormat(cmd, value) {
    document.execCommand(cmd, false, value || null);
  }

  function insertLink(url) {
    if (!url) return;
    document.execCommand('createLink', false, url);
    // Make links open in new tab
    setTimeout(() => {
      document.querySelectorAll('a[href]').forEach(a => { a.target = '_blank'; a.rel = 'noopener noreferrer'; });
    }, 0);
  }

  // ── image upload ──────────────────────────────────────────────────────────────
  function handleImageUpload(blockId) {
    imageUploadTarget.current = blockId;
    const inp = imgInputs.current[blockId];
    if (inp) inp.click();
  }

  function handleImageChange(blockId, file) {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => {
      setBlocks(bs => bs.map(b => b.id === blockId ? { ...b, type: 'image', src: ev.target.result } : b));
    };
    reader.readAsDataURL(file);
  }

  // ── render ────────────────────────────────────────────────────────────────────
  return (
    <div style={{ maxWidth: 720, margin: '0 auto', padding: '2rem 2.5rem', position: 'relative', background: '#fff', minHeight: 'calc(100vh - 120px)', borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
      {/* Hover styles for block delete button */}
      <style>{`
        .be-block-row .be-block-delete { opacity: 0; transition: opacity 0.15s; }
        .be-block-row:hover .be-block-delete { opacity: 1; color: #E96A6A !important; }
        @media (max-width: 768px) {
          .be-block-row .be-block-delete { opacity: 0.5; right: 4px !important; top: -2px !important; }
        }
      `}</style>

      {/* Page emoji */}
      <div style={{ position: 'relative', display: 'inline-block', marginBottom: 8 }}>
        <button
          onClick={() => setShowEmojiPicker(p => !p)}
          title={emoji ? 'Change emoji' : 'Add emoji'}
          style={{
            fontSize: '2.2rem', background: 'transparent', border: 'none', cursor: 'pointer',
            lineHeight: 1, padding: '2px 4px', borderRadius: 6,
            opacity: emoji ? 1 : 0.3,
          }}
        >{emoji || '📄'}</button>
        {showEmojiPicker && (
          <EmojiPicker
            onPick={e => { setEmoji(e); setShowEmojiPicker(false); }}
            onClose={() => setShowEmojiPicker(false)}
          />
        )}
      </div>

      {/* Page title */}
      <input
        value={title}
        onChange={e => setTitle(e.target.value)}
        placeholder="Untitled"
        style={{
          display: 'block', width: '100%', border: 'none', outline: 'none',
          fontSize: '2.2rem', fontWeight: 800, color: NAVY,
          fontFamily: 'Raleway, sans-serif', marginBottom: '1.5rem',
          background: 'transparent', boxSizing: 'border-box',
        }}
      />

      {/* Formatting toolbar (floating, appears on selection) */}
      {fmtBar && (
        <FormatToolbar
          rect={fmtBar.rect}
          onFormat={execFormat}
          onLink={insertLink}
          onClose={() => setFmtBar(null)}
        />
      )}

      {/* Blocks */}
      <div>
        {blocks.map((block, idx) => {
          const isOnlyBlock = blocks.length === 1;
          const pl = block.type === 'paragraph' && idx === 0 && !block.html
            ? "Type something, or '/' for a command..."
            : undefined;
          return (
            <div
              key={block.id}
              className="be-block-row"
              style={{ position: 'relative', marginBottom: 2 }}
              onFocus={() => setActiveId(block.id)}
              onBlur={() => { syncBlock(block.id); }}
            >
              <Block
                block={block}
                blockRef={el => setBlockRef(block.id, el)}
                onKeyDown={handleKeyDown}
                onInput={handleInput}
                onChange={updated => setBlocks(bs => bs.map(b => b.id === updated.id ? updated : b))}
                onImageUpload={handleImageUpload}
                isOnly={isOnlyBlock}
                placeholder={pl}
              />
              {/* Delete block button — visible on hover, hidden when only 1 block */}
              {!isOnlyBlock && (
                <button
                  title="Delete block"
                  onClick={() => deleteBlock(block.id)}
                  className="be-block-delete"
                  style={{
                    position: 'absolute', top: 2, right: -28,
                    background: 'none', border: 'none', color: '#ccc',
                    cursor: 'pointer', fontSize: '0.8rem', padding: '2px 5px',
                    borderRadius: 4, lineHeight: 1,
                  }}
                >✕</button>
              )}
              {/* Hidden file input per image block */}
              {block.type === 'image' && (
                <input
                  key={`img-${block.id}`}
                  ref={el => { if (el) imgInputs.current[block.id] = el; }}
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={e => handleImageChange(block.id, e.target.files?.[0])}
                />
              )}
            </div>
          );
        })}

        {/* + add block hint — on mobile, opens slash menu */}
        <button
          onClick={() => {
            const lastId = blocks[blocks.length - 1]?.id || '';
            // Insert a new block, then open the slash menu for it on the next tick
            flushAll();
            const fresh = makeBlock();
            setBlocks(bs => {
              const idx = bs.findIndex(b => b.id === lastId);
              const next = [...bs];
              next.splice(idx + 1, 0, fresh);
              return next;
            });
            setTimeout(() => {
              const el = blockRefs.current[fresh.id];
              if (el) {
                el.focus();
                placeCursorAtStart(el);
                const rect = el.getBoundingClientRect();
                setSlash({ blockId: fresh.id, query: '', x: rect.left, y: rect.bottom + 4 });
              }
            }, 0);
          }}
          style={{
            marginTop: 12, background: 'none', border: `1px dashed ${BORDER}`,
            borderRadius: 6, color: '#bbb', fontSize: '0.83rem', cursor: 'pointer',
            padding: '6px 14px', width: '100%', textAlign: 'left',
          }}
        >
          + Add block · press <strong style={{ color: '#999' }}>/</strong> for tools
        </button>
      </div>

      {/* Slash command menu */}
      {slash && (
        <SlashMenu
          x={slash.x} y={slash.y}
          query={slash.query}
          onSelect={handleSlashSelect}
          onClose={() => setSlash(null)}
        />
      )}
    </div>
  );
}

// ── Cursor utils ──────────────────────────────────────────────────────────────
function placeCursorAtEnd(el) {
  const range = document.createRange();
  const sel = window.getSelection();
  range.selectNodeContents(el);
  range.collapse(false);
  sel.removeAllRanges();
  sel.addRange(range);
}

function placeCursorAtStart(el) {
  const range = document.createRange();
  const sel = window.getSelection();
  range.selectNodeContents(el);
  range.collapse(true);
  sel.removeAllRanges();
  sel.addRange(range);
}
